import { IBaseFormElementProps, BaseFormElement } from "./BaseFormElement";
import { IValidationRule } from "./Validation/IValidationRule";
import { Validator } from "./Validation/Validator";
import { ObjectComparer } from "../Utils/ObjectComparer";
import { isNullOrEmpty } from "../Utils/Arrays";

export interface IBaseListInputProps<TOption> extends IBaseFormElementProps {
    selected?: TOption;

    options: TOption[];

    getText: (option: TOption) => string;

    onChange: (newValue: TOption) => void;

    onBlur?: (newValue: TOption) => void;

    disabled?: boolean;

    required?: string;

    id?: string;

    cssInput?: string;

    tabIndex?: number;

    validationRules?: IValidationRule<TOption>[];

    alwaysValidateOnUpdate?: boolean;
}

export interface IBaseListInputState<TOption> {
    selectedIndex?: number;

    pristineValue?: TOption; // TODO should be a pristineIndex? what if array changes inbetween??

    isValid?: boolean;

    isPristine?: boolean;

    disable?: boolean;
}

export abstract class BaseListInput<TOption, TProps extends IBaseListInputProps<any>> extends BaseFormElement<
    TProps,
    IBaseListInputState<TOption>
> {
    protected validator: Validator<TOption>;

    protected options: TOption[] = [];

    constructor(props: TProps) {
        super(props);
        this.state = this.getDefaultState(props);
        this.options = this.props.options && this.props.options.slice();

        this.validator = new Validator<TOption>();
        this.initValidation();
    }

    public abstract render(): JSX.Element;

    public isValid(): boolean {
        const value = this.getSelectedOption();
        return this.runValidation(value, true);
    }

    public setDisable() {
        this.setState({ disable: true });
    }

    public componentWillReceiveProps(nextProps: TProps): void {
        // If the value passed has changed, set it. If value is not passed it will
        // internally update, and this will never run
        if (
            !ObjectComparer.isSame(this.props.selected, nextProps.selected) &&
            !ObjectComparer.isSame(nextProps.selected, this.getSelectedOption(this.state.selectedIndex))
        ) {
            const selectedIndex = this.options && this.options.indexOf(nextProps.selected);
            this.setSelected(selectedIndex, true);
        }
        if (this.options && nextProps.options && ObjectComparer.arraysDiffer(this.options, nextProps.options)) {
            this.options = nextProps.options && nextProps.options.slice();
            const selectedIndex = this.options && this.options.indexOf(nextProps.selected);
            this.setSelected(selectedIndex, true);
        }
    }

    public componentDidUpdate(prevProps: TProps): void {
        if (
            (!ObjectComparer.isSame(this.props.selected, prevProps.selected) && !this.state.isPristine) ||
            this.props.alwaysValidateOnUpdate
        ) {
            this.isValid();
        }
    }

    public shouldComponentUpdate(nextProps: TProps, nextState: IBaseListInputState<TOption>): boolean {
        return (
            ObjectComparer.objectsDiffer(this.props, nextProps) ||
            ObjectComparer.objectsDiffer(this.options, nextProps.options) ||
            ObjectComparer.objectsDiffer(this.state, nextState)
        );
    }

    protected initValidation(): void {
        this.validator.clear();

        this.validator.add((value: TOption) => value !== null && value !== undefined, this.props.required);
        this.validator.addRange(this.props.validationRules);
    }

    protected abstract getSelectedOption(selectedIndex?: number): TOption;

    protected abstract isHidden(): boolean;

    protected onChange(syntheticEvent: React.SyntheticEvent): void {
        const selectedIndexString = (syntheticEvent.target as any).value;
        const selectedIndex = Globalize.parseInt(selectedIndexString);
        const value = this.getSelectedOption(selectedIndex);
        this.setSelected(selectedIndex, false);
        if (this.props.onChange) {
            this.runValidation(value);
            this.props.onChange(value);
        }
    }

    protected onBlur(): void {
        const value = this.getSelectedOption();
        if (this.props.onBlur) {
            this.runValidation(value);
            this.props.onBlur(value);
        }
    }

    protected runValidation(value: TOption, validationTriggered?: boolean): boolean {
        if (this.isHidden()) {
            return true;
        }

        this.initValidation();

        const errors = this.validator.getValidationErrors(value);
        this.triggerValidationResultChange(errors, validationTriggered);

        const isValid = isNullOrEmpty(errors);
        this.setState({ isValid });

        return isValid;
    }

    private getDefaultState(props: TProps): IBaseListInputState<TOption> {
        const selectedIndex = this.props.options && this.props.options.indexOf(this.props.selected);
        return {
            selectedIndex,
            isValid: true,
            isPristine: true,
            pristineValue: props.selected,
        };
    }

    private setSelected(index: number, isPristine: boolean): void {
        this.setState({
            selectedIndex: index,
            isPristine,
        });
    }
}
